// Client-side half of the label page's variant search. The ranking itself
// comes from the backend's `productVariantSearch` (the same Meilisearch index
// the storefront searches), which returns every variant of each matched
// product unless the query is an exact SKU. These helpers narrow those hits
// to the variants the query actually names — "espaçador 8mm" should offer the
// 8mm spacer, not all six sizes — the way the storefront does for its cards.

/**
 * Same folding as the storefront's normalizeForSearch and the backend's
 * normalize_for_search (saleor/product/meili.py), so a query matches here
 * exactly when it matches there: case/accents, decimal and dimension
 * separators, Ø/º symbols and spaced units ("76 mm", "3''", "1 metro").
 */
export const normalizeForSearch = (value: string): string =>
  (value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[*×]/g, "x")
    .replace(/,/g, ".")
    .replace(/ø/g, " ")
    .replace(/[º°]/g, " ")
    .replace(/(\d)\s*(?:milimetros?|mm)\b/g, "$1mm")
    .replace(/(\d)\s*(?:centimetros?|cm)\b/g, "$1cm")
    .replace(/(\d)\s*(?:metros?|m)\b/g, "$1m")
    .replace(/(\d)\s*(?:polegadas?|pol|["”″]|’’|''|′′)/g, "$1pol")
    .replace(/(\d)\s*x\s*(\d)/g, "$1x$2")
    .replace(/\s+/g, " ")
    .trim();

const tokenize = (normalized: string): string[] => normalized.split(/[\s\-_/()]+/).filter(Boolean);

const blobHasTerm = (blob: string, tokens: string[], term: string) =>
  tokens.some(token => token.startsWith(term)) || (term.length >= 3 && blob.includes(term));

interface NarrowableVariant {
  name?: string | null;
  sku?: string | null;
}

/**
 * Keeps the variants of one product that match the query terms the product
 * itself doesn't explain. Terms found in the product name ("espacador") say
 * nothing about which variant is wanted; the rest ("8mm", "preto") must then
 * appear in the variant's own name or SKU. Falls back to every variant when
 * no variant matches, since the backend matched the product for a reason
 * (a typo, the description) this simple check can't see.
 */
export const narrowVariantsToQuery = <T extends NarrowableVariant>(
  productName: string,
  variants: T[],
  query: string,
): T[] => {
  const terms = tokenize(normalizeForSearch(query));
  const productBlob = normalizeForSearch(productName);
  const productTokens = tokenize(productBlob);
  const variantTerms = terms.filter(term => !blobHasTerm(productBlob, productTokens, term));

  if (variantTerms.length === 0 || variants.length <= 1) {
    return variants;
  }

  const narrowed = variants.filter(variant => {
    const blob = normalizeForSearch(`${variant.name ?? ""} ${variant.sku ?? ""}`);
    const tokens = tokenize(blob);

    return variantTerms.every(term => blobHasTerm(blob, tokens, term));
  });

  return narrowed.length > 0 ? narrowed : variants;
};

/**
 * Strict check used on ops: every query term must appear in the product name,
 * variant name or SKU. Drops the engine's typo-tolerant and description-only
 * matches, so operators only see results that literally contain what they typed.
 */
export const variantMatchesAllTerms = (
  productName: string,
  variant: NarrowableVariant,
  query: string,
): boolean => {
  const terms = tokenize(normalizeForSearch(query));
  const blob = normalizeForSearch(`${productName} ${variant.name ?? ""} ${variant.sku ?? ""}`);
  const tokens = tokenize(blob);

  return terms.length > 0 && terms.every(term => blobHasTerm(blob, tokens, term));
};

/** The backend collapses an exact SKU query to that one variant. */
export const isExactSkuQuery = (query: string, sku: string | null | undefined) =>
  !!sku && query.trim().toLowerCase() === sku.trim().toLowerCase();
